import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { FaArrowLeft, FaCheckCircle, FaPlay, FaSearch } from 'react-icons/fa';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { isKickoffEligibleClient, normalizeKickoffStatus } from '../../utils/crmClientListFilters';
import { fetchAdditionalTeamMemberNamesByAccountIds } from '../../services/accountTeamMembersService';
import PortalBackground from '../../components/PortalBackground';
import BBALoader from '../../components/BBALoader';

const FulfillmentKickoff = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('in_review');

  useEffect(() => {
    const loadClients = async () => {
      setLoading(true);
      try {
        const accountsRef = collection(db, 'accounts');
        let querySnapshot;
        try {
          const q = query(accountsRef, where('type', '==', 'Clients'));
          querySnapshot = await getDocs(q);
        } catch (err) {
          querySnapshot = await getDocs(accountsRef);
        }
        const list = querySnapshot.docs
          .map((doc) => {
            const data = doc.data();
            return { id: doc.id, ...data };
          })
          .filter((client) => isKickoffEligibleClient(client))
          .map((client) => {
            const contact = client.contact || {};
            const contactName =
              contact.name ||
              (contact.firstName && contact.lastName
                ? `${contact.firstName} ${contact.lastName}`
                : contact.firstName || contact.lastName || 'No contact name');
            return {
              id: client.id,
              company: client.company || 'No company',
              contactName,
              contactEmail: String(contact.email || client.email || '').trim(),
              kickoffStatus: normalizeKickoffStatus(client),
            };
          });
        let enriched = list;
        try {
          const primaryById = new Map(
            list.map((c) => [c.id, { contactName: c.contactName, contactEmail: c.contactEmail }])
          );
          const teamMap = await fetchAdditionalTeamMemberNamesByAccountIds(primaryById);
          enriched = list.map((c) => ({
            id: c.id,
            company: c.company,
            contactName: c.contactName,
            kickoffStatus: c.kickoffStatus,
            additionalTeamNames: teamMap.get(c.id) ?? [],
          }));
        } catch (teamErr) {
          console.warn('Could not load team members for kickoff list:', teamErr);
          enriched = list.map((c) => ({
            id: c.id,
            company: c.company,
            contactName: c.contactName,
            kickoffStatus: c.kickoffStatus,
            additionalTeamNames: [],
          }));
        }
        setClients(enriched);
      } catch (error) {
        console.error('Error loading clients for kickoff:', error);
        setClients([]);
      } finally {
        setLoading(false);
      }
    };
    loadClients();
  }, []);

  const filteredClients = useMemo(() => {
    const byStatus = clients.filter((c) => c.kickoffStatus === statusFilter);
    const q = searchQuery.trim().toLowerCase();
    if (!q) return byStatus;
    return byStatus.filter((c) => {
      const company = String(c.company || '').toLowerCase();
      const name = String(c.contactName || '').toLowerCase();
      const team = (c.additionalTeamNames || []).join(' ').toLowerCase();
      return company.includes(q) || name.includes(q) || team.includes(q);
    });
  }, [clients, searchQuery, statusFilter]);

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center justify-center relative">
            <button
              onClick={() => navigate('/crm/fulfillment')}
              className={`absolute left-0 flex items-center justify-center w-10 h-10 rounded-md transition-colors ${
                darkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-white' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
              title="Back to Fulfillment"
            >
              <FaArrowLeft className="w-5 h-5" />
            </button>
            <div className="text-center">
              <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white mb-2">Kickoff</h1>
              <p className="text-gray-600 dark:text-gray-300">Manage client kickoff and onboarding</p>
            </div>
          </div>
        </div>

        {!loading && clients.length > 0 && (
          <div className="mb-6 flex w-full max-w-4xl mx-auto flex-col gap-4 sm:flex-row sm:items-center">
            <div className="relative min-w-0 flex-1">
              <label htmlFor="kickoff-client-search" className="sr-only">
                Search by company or client name
              </label>
              <FaSearch
                className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}
                aria-hidden
              />
              <input
                id="kickoff-client-search"
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search company or client name…"
                autoComplete="off"
                className={`w-full rounded-lg border py-2.5 pl-10 pr-4 text-sm shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                  darkMode
                    ? 'border-gray-600 bg-gray-800 text-white placeholder:text-gray-500'
                    : 'border-gray-300 bg-white text-gray-900 placeholder:text-gray-400'
                }`}
              />
            </div>
            <div className="flex shrink-0 items-center gap-2 sm:justify-end">
              <label
                htmlFor="kickoff-status-filter"
                className={`text-sm whitespace-nowrap ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}
              >
                Status
              </label>
              <select
                id="kickoff-status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={`min-w-[11rem] rounded-lg border py-2.5 px-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                  darkMode
                    ? 'border-gray-600 bg-gray-800 text-white'
                    : 'border-gray-300 bg-white text-gray-900'
                }`}
              >
                <option value="in_review">In Review</option>
                <option value="complete">Completed</option>
              </select>
            </div>
          </div>
        )}

        {loading ? (
          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-12 text-center`}>
            <BBALoader size={48} className="mx-auto" />
            <p className="text-gray-600 dark:text-gray-400 mt-4">Loading clients...</p>
          </div>
        ) : clients.length > 0 ? (
          filteredClients.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredClients.map((client) => {
                const isComplete = client.kickoffStatus === 'complete';
                return (
                  <div
                    key={client.id}
                    onClick={() => navigate(`/crm/fulfillment/kickoff/${client.id}`)}
                    className={`rounded-lg shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow border-l-4 ${
                      isComplete
                        ? darkMode
                          ? 'border-green-400 bg-green-900/35 ring-1 ring-green-700/50'
                          : 'border-green-600 bg-green-50 ring-1 ring-green-200'
                        : `${darkMode ? 'bg-gray-800' : 'bg-white'} border-green-500`
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{client.company}</h3>
                      {isComplete ? (
                        <FaCheckCircle
                          className="h-7 w-7 shrink-0 text-green-600 dark:text-green-400"
                          aria-label="Kickoff completed"
                        />
                      ) : null}
                    </div>
                    <p className="text-gray-600 dark:text-gray-400">{client.contactName}</p>
                    {client.additionalTeamNames?.length ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {client.additionalTeamNames.join(', ')}
                      </p>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-12 text-center`}>
              <FaSearch className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">No matches</h2>
              <p className="text-gray-600 dark:text-gray-400">
                {searchQuery.trim()
                  ? `Nothing matched "${searchQuery.trim()}" for ${statusFilter === 'complete' ? 'completed' : 'in review'} kickoffs. Try another name.`
                  : statusFilter === 'complete'
                    ? 'No completed kickoffs in this list.'
                    : 'No kickoffs in review.'}
              </p>
            </div>
          )
        ) : (
          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-12 text-center`}>
            <FaPlay className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">No Clients</h2>
            <p className="text-gray-600 dark:text-gray-400">
              No clients with an ongoing program — canceled, unpaid, and Lost clients are hidden.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FulfillmentKickoff;
